"use client";
import React, { useMemo } from "react";
import useActiveSection from "@/hooks/useActiveSection";
import { useGetRecommendedFormations } from "@/actions/formation";


export interface AnchorNavSection {
  label: string;
  id: string;
}

/** The fiche's anchor targets, in the order the pills present them. */
export const FICHE_SECTIONS: AnchorNavSection[] = [
  { label: "Présentation", id: "presentation" },
  { label: "Programme détaillé", id: "programme" },
  { label: "Recommandations", id: "recommended" },
  { label: "Évaluations", id: "évaluations" },
  { label: "FAQ", id: "faq" },
  { label: "Financements", id: "financements" },
];

export function useFicheSections(slug: string): AnchorNavSection[] {
  const { recommendedFormationsEmpty, recommendedFormationsLoading } = useGetRecommendedFormations(slug);

  return useMemo(
    () =>
      recommendedFormationsLoading || recommendedFormationsEmpty
        ? FICHE_SECTIONS.filter((section) => section.id !== "recommended")
        : FICHE_SECTIONS,
    [recommendedFormationsLoading, recommendedFormationsEmpty]
  );
}

interface FicheAnchorNavProps {
  /** Spacing classes for the row — set by each host (in-flow / sticky header). */
  className?: string;
  /** Pills to render. Defaults to the fiche's own sections. */
  sections?: readonly AnchorNavSection[];
  /** Landmark name — override when the row isn't a formation's. */
  ariaLabel?: string;
}

/**
 * The pill row that jumps between a page's sections. On the fiche it is
 * rendered twice: in-flow under the summary cards (FichePresentation) and
 * inside the mobile sticky header (FicheStickyNav) — both read the active pill
 * from the scroll position, so the two rows always agree. The coaching landing
 * pages pass their own `sections`.
 */
const FicheAnchorNav: React.FC<FicheAnchorNavProps> = ({
  className = "",
  sections = FICHE_SECTIONS,
  ariaLabel = "Sections de la formation",
}) => {
  const activeSection = useActiveSection(sections.map((section) => section.id));

  return (
    <nav
      aria-label={ariaLabel}
      className={`flex items-center justify-center-safe overflow-x-auto overscroll-x-contain no-scrollbar ${className}`}
    >
      {sections.map(({ label, id }) => (
        <button
          key={id}
          type="button"
          onClick={() =>
            document
              .getElementById(id)
              ?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
          aria-current={activeSection === id ? "true" : undefined}
          className={`shrink-0 cursor-pointer md:px-6 px-3.5 md:py-4 py-2 rounded-full text-[11px] md:text-xs font-bold leading-4 transition-colors whitespace-nowrap border border-[#EFEFEF] ${
            activeSection === id
              ? "bg-[#1B365E] text-white"
              : "bg-white text-[#1B365E] hover:bg-[#C7F8FF]/40"
          }`}
        >
          {label}
        </button>
      ))}
    </nav>
  );
};

export default FicheAnchorNav;
